import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Camera, Save, ChevronLeft, CheckCircle2, Circle, AlertCircle, Wrench, Clipboard, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = axios.create({ baseURL: API, withCredentials: true });

function errMsg(e) {
  return e?.response?.data?.detail || e?.message || "Erro";
}

function PinKeypad({ value, onChange, disabled }) {
  const push = (d) => { if (!disabled && value.length < 4) onChange(value + d); };
  const back = () => { if (!disabled && value.length) onChange(value.slice(0, -1)); };
  const btn = "h-14 rounded-xl bg-zinc-900 text-zinc-100 text-xl font-bold border border-zinc-800 active:scale-95 transition-transform disabled:opacity-50";
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-3" aria-label="PIN digitado">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} data-testid={`pin-dot-${i}`}
               className={`h-5 w-5 rounded-full border ${i < value.length ? "bg-green-500 border-green-500" : "border-zinc-700"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {["1","2","3","4","5","6","7","8","9"].map((d) => (
          <button key={d} type="button" data-testid={`pin-key-${d}`} onClick={() => push(d)} disabled={disabled} className={btn}>{d}</button>
        ))}
        <button type="button" data-testid="pin-key-back" onClick={back} disabled={disabled} className={`${btn} text-sm`}>Apagar</button>
        <button type="button" data-testid="pin-key-0" onClick={() => push("0")} disabled={disabled} className={btn}>0</button>
        <div />
      </div>
    </div>
  );
}

function MechLogin({ slug, redirectOrder }) {
  const [data, setData] = useState(null);
  const [emp, setEmp] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    m.get(`/m/${slug}/mechanics`).then(({ data }) => setData(data))
      .catch((e) => setErr(errMsg(e)));
  }, [slug]);

  useEffect(() => { if (pin.length === 4 && emp) { submit(); } /* eslint-disable-next-line */ }, [pin]);

  const submit = async () => {
    if (!emp || pin.length !== 4) return;
    setBusy(true); setErr("");
    try {
      await m.post(`/m/${slug}/login`, { employee_id: emp.id, pin });
      toast.success(`Olá, ${emp.name}!`);
      nav(redirectOrder ? `/mecanico/${slug}/os/${redirectOrder}` : `/mecanico/${slug}`, { replace: true });
      window.location.reload();
    } catch (e) {
      setErr(errMsg(e)); setPin("");
    } finally { setBusy(false); }
  };

  if (err && !data) {
    return <div data-testid="mech-login-error" className="p-6 text-center text-red-300">{err}</div>;
  }
  if (!data) return <div className="p-6 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div className="max-w-md mx-auto p-5 space-y-5">
      <header className="text-center">
        <div className="inline-flex items-center gap-2 text-green-400 text-xs font-bold uppercase tracking-widest"><Wrench className="h-4 w-4" /> Área do mecânico</div>
        <h1 className="font-display text-2xl font-black uppercase text-zinc-100 mt-1">{data.shop.name}</h1>
        <p className="text-sm text-zinc-400">Toque no seu nome e digite o PIN</p>
      </header>

      {!emp ? (
        <div data-testid="mech-list" className="space-y-2">
          {data.mechanics.length === 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-sm text-zinc-300">
              Nenhum mecânico com PIN cadastrado. Peça ao dono para liberar o acesso em RH.
            </div>
          )}
          {data.mechanics.map((x) => (
            <button key={x.id} data-testid={`mech-pick-${x.id}`} disabled={x.blocked}
                    onClick={() => setEmp(x)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-left hover:border-green-brand disabled:opacity-40">
              <div className="min-w-0">
                <div className="font-bold text-zinc-100 truncate">{x.name}</div>
                {x.blocked && <div className="text-[11px] text-amber-300">PIN bloqueado</div>}
              </div>
              <span className="text-xs font-mono-caps text-zinc-500">entrar</span>
            </button>
          ))}
        </div>
      ) : (
        <div data-testid="mech-pin-step" className="space-y-4">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 flex items-center justify-between">
            <div><div className="text-[11px] text-zinc-500 font-mono-caps">Entrando como</div><div className="font-bold text-zinc-100">{emp.name}</div></div>
            <button type="button" onClick={() => { setEmp(null); setPin(""); setErr(""); }} data-testid="mech-change" className="text-xs text-zinc-400 underline">Trocar</button>
          </div>
          <PinKeypad value={pin} onChange={setPin} disabled={busy} />
          {err && <div data-testid="mech-pin-error" className="text-sm text-red-300 text-center">{err}</div>}
          {busy && <div className="text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
        </div>
      )}
    </div>
  );
}

function LaudoCard({ orderId, order, onChange }) {
  const [open, setOpen] = useState(false);
  const [laudo, setLaudo] = useState({ diagnosis: "", cause: "", solution: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inp = useRef(null);
  useEffect(() => {
    const tr = order?.tech_report || {};
    setLaudo({ diagnosis: tr.diagnosis || "", cause: tr.cause || "", solution: tr.solution || "" });
  }, [order?.id, order?.tech_report?.updated_at]);
  const laudoPhotos = (order?.photos || []).filter((p) => p.category === "laudo");
  const save = async () => {
    setSaving(true);
    try {
      await m.put(`/m/orders/${orderId}/tech-report`, laudo);
      toast.success("Laudo salvo");
      await onChange?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const addPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await m.post(`/m/orders/${orderId}/photos`, fd, { params: { category: "laudo" } });
      toast.success("Foto do laudo enviada");
      await onChange?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); if (inp.current) inp.current.value = ""; }
  };
  const dirty = (() => {
    const tr = order?.tech_report || {};
    return (tr.diagnosis || "") !== laudo.diagnosis || (tr.cause || "") !== laudo.cause || (tr.solution || "") !== laudo.solution;
  })();
  return (
    <section data-testid="mech-laudo" className="rounded-xl border border-zinc-800 bg-zinc-950/40">
      <button type="button" data-testid="mech-laudo-toggle" onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-3 text-left">
        <div className="flex items-center gap-2 text-zinc-100 font-bold text-sm"><Clipboard className="h-4 w-4 text-amber-300" /> Laudo técnico</div>
        <span className="text-xs text-zinc-500">{open ? "fechar" : (laudo.diagnosis || laudo.cause || laudo.solution || laudoPhotos.length ? "preenchido" : "abrir")}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {[["diagnosis","Diagnóstico (o que você encontrou)"],["cause","Causa provável"],["solution","Solução recomendada"]].map(([k, lbl]) => (
            <div key={k}>
              <div className="text-[11px] text-zinc-500 mb-1">{lbl}</div>
              <textarea data-testid={`mech-laudo-${k}`} value={laudo[k]} onChange={(e) => setLaudo((p) => ({ ...p, [k]: e.target.value }))}
                        rows={3} className="w-full rounded-md border border-zinc-800 bg-zinc-900 p-2 text-sm text-zinc-100" />
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" data-testid="mech-laudo-save" onClick={save} disabled={saving || !dirty}
                    className="flex-1 h-10 rounded-md bg-green-brand text-white font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar laudo
            </button>
            <input ref={inp} type="file" accept="image/*" capture="environment" hidden onChange={(e) => addPhoto(e.target.files?.[0])} data-testid="mech-laudo-photo-input" />
            <button type="button" data-testid="mech-laudo-photo-btn" onClick={() => inp.current?.click()} disabled={uploading}
                    className="h-10 px-3 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-100 text-sm inline-flex items-center gap-1">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Foto
            </button>
          </div>
          {laudoPhotos.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="mech-laudo-photos">
              {laudoPhotos.map((p) => (
                <img key={p.id} src={`${API}/m/orders/${orderId}/photos/${p.id}`} alt="foto laudo" className="aspect-square object-cover rounded-md border border-zinc-800" />
              ))}
            </div>
          )}
          {order?.tech_report?.mechanic_name && (
            <div className="text-[11px] text-zinc-500">Por {order.tech_report.mechanic_name}{order.tech_report.updated_at ? ` · ${new Date(order.tech_report.updated_at).toLocaleString("pt-BR")}` : ""}</div>
          )}
        </div>
      )}
    </section>
  );
}

const INTAKE_LABEL = { ok: "OK", bom: "OK", atencao: "Atenção", ruim: "Problema", na: "N/A" };

function OrderInfo({ order }) {
  const servs = (order.items || []).filter((i) => i.kind !== "p");
  const parts = (order.items || []).filter((i) => i.kind === "p");
  const intake = (order.intake_checklist || []).filter((c) => c.value && c.value !== "na");
  const qty = (q) => `${Number(q || 0).toLocaleString("pt-BR")}x`;
  const List = ({ title, rows, tid }) => rows.length ? (
    <div data-testid={tid}>
      <div className="text-[11px] text-zinc-500 font-mono-caps mb-1">{title}</div>
      <ul className="space-y-1">{rows.map((r, i) => <li key={i} className="text-sm text-zinc-100 flex gap-2"><span className="text-zinc-500 shrink-0">{qty(r.quantity)}</span>{r.description}</li>)}</ul>
    </div>
  ) : null;
  return (
    <section data-testid="mech-order-info" className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3 space-y-3">
      <div className="text-sm text-zinc-300" data-testid="mech-order-customer">Cliente: <span className="font-bold text-zinc-100">{order.customer_name || "—"}</span></div>
      <List title="Serviços a fazer" rows={servs} tid="mech-order-services" />
      <List title="Peças" rows={parts} tid="mech-order-parts" />
      {!servs.length && !parts.length && <div className="text-xs text-zinc-500">Sem itens no orçamento ainda.</div>}
      {(order.description || order.intake_notes || order.mechanic_notes) && (
        <div data-testid="mech-order-notes" className="text-sm text-zinc-300 space-y-1">
          <div className="text-[11px] text-zinc-500 font-mono-caps">Observações</div>
          {order.description && <p>{order.description}</p>}
          {order.intake_notes && <p>{order.intake_notes}</p>}
          {order.mechanic_notes && <p>{order.mechanic_notes}</p>}
        </div>
      )}
      {intake.length > 0 && (
        <div data-testid="mech-order-intake">
          <div className="text-[11px] text-zinc-500 font-mono-caps mb-1">Checklist de entrada</div>
          <ul className="grid grid-cols-2 gap-1">{intake.map((c, i) => <li key={i} className="text-xs text-zinc-300">{c.label}: <b className="text-zinc-100">{INTAKE_LABEL[c.value] || c.value}</b>{c.note ? ` — ${c.note}` : ""}</li>)}</ul>
        </div>
      )}
    </section>
  );
}

const STATE_STYLE = {
  bom: { label: "Bom", cls: "bg-green-600 text-white" },
  ruim: { label: "Trocar", cls: "bg-red-600 text-white" },
};

function ChecklistEditor({ orderId, slug, onBack }) {
  const [tpl, setTpl] = useState(null);
  const [order, setOrder] = useState(null);
  const [entries, setEntries] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inp = useRef(null);

  useEffect(() => {
    Promise.all([m.get("/m/tech-checklist/template"), m.get(`/m/orders/${orderId}`)])
      .then(([t, o]) => {
        setTpl(t.data.sections || []); setOrder(o.data);
        const map = {}; (o.data.tech_checklist?.entries || []).forEach((e) => {
          map[`${e.section}||${e.item}`] = { state: e.state || null, note: e.note || "" };
        });
        setEntries(map);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [orderId]);

  const setItem = (sec, it, patch) => setEntries((p) => ({ ...p, [`${sec}||${it}`]: { ...(p[`${sec}||${it}`] || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { entries: Object.entries(entries).map(([k, v]) => {
        const [section, item] = k.split("||");
        return { section, item, state: v.state || null, note: (v.note || "").trim() || null };
      }).filter((e) => e.state || e.note) };
      await m.put(`/m/orders/${orderId}/tech-checklist`, body);
      toast.success("Checklist salvo");
      onBack?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await m.post(`/m/orders/${orderId}/photos`, fd, { params: { category: "service" } });
      toast.success("Foto enviada");
      const { data } = await m.get(`/m/orders/${orderId}`);
      setOrder(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); if (inp.current) inp.current.value = ""; }
  };

  if (!tpl || !order) return <div className="p-6 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  const totalItems = tpl.reduce((n, s) => n + s.items.length, 0);
  const marked = Object.values(entries).filter((e) => e.state).length;

  return (
    <div className="max-w-xl mx-auto p-4 pb-24 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button data-testid="mech-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-zinc-300"><ChevronLeft className="h-4 w-4" /> Voltar</button>
        <div className="text-right">
          <div className="font-display text-base font-black uppercase text-zinc-100">OS #{order.order_number || "—"}</div>
          <div className="text-[11px] text-zinc-500">{order.vehicle?.plate} · {order.vehicle?.model || ""}</div>
        </div>
      </div>

      <OrderInfo order={order} />

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 text-xs text-zinc-300" data-testid="mech-progress">
        {marked} de {totalItems} itens marcados
      </div>

      <input ref={inp} type="file" accept="image/*" capture="environment" hidden onChange={(e) => addPhoto(e.target.files?.[0])} data-testid="mech-photo-input" />
      <button type="button" onClick={() => inp.current?.click()} disabled={uploading} data-testid="mech-photo-btn"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl border border-zinc-800 bg-zinc-900 text-zinc-100 font-bold">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Tirar foto
      </button>
      {order.photos?.length ? (
        <div className="grid grid-cols-4 gap-2" data-testid="mech-photos">
          {order.photos.slice(0, 12).map((p) => (
            <img key={p.id} src={`${API}/m/orders/${orderId}/photos/${p.id}`} alt="foto" className="aspect-square object-cover rounded-lg border border-zinc-800" />
          ))}
        </div>
      ) : null}

      <LaudoCard orderId={orderId} order={order} onChange={async () => { try { const { data } = await m.get(`/m/orders/${orderId}`); setOrder(data); } catch {} }} />

      <div className="space-y-4">
        {tpl.map((sec, si) => (
          <section key={si} data-testid={`mech-sec-${si}`} className="rounded-xl border border-zinc-800 bg-zinc-950/40">
            <div className="px-3 py-2 border-b border-zinc-800 font-bold text-zinc-100 text-sm">{sec.title}</div>
            <div className="divide-y divide-zinc-800/80">
              {sec.items.map((it, ii) => {
                const key = `${sec.title}||${it}`;
                const cur = entries[key] || {};
                return (
                  <div key={ii} data-testid={`mech-item-${si}-${ii}`} className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm text-zinc-100 min-w-0">{it}</div>
                      <div className="flex gap-1 shrink-0">
                        {["bom","ruim"].map((st) => {
                          const on = cur.state === st; const S = STATE_STYLE[st];
                          return (
                            <button key={st} type="button" data-testid={`mech-item-${si}-${ii}-${st}`}
                              onClick={() => setItem(sec.title, it, { state: on ? null : st })}
                              className={`h-8 px-2 rounded-md text-[11px] font-bold uppercase border ${on ? S.cls + " border-transparent" : "border-zinc-800 text-zinc-300"}`}>
                              {on ? <CheckCircle2 className="h-3 w-3 inline mr-1" /> : <Circle className="h-3 w-3 inline mr-1" />}{S.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {cur.state === "ruim" && (
                      <input data-testid={`mech-item-${si}-${ii}-note`} placeholder="Observação (opcional)" value={cur.note || ""} onChange={(e) => setItem(sec.title, it, { note: e.target.value })}
                             className="w-full h-9 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100" />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 p-3">
        <button data-testid="mech-save" onClick={save} disabled={saving}
                className="w-full h-12 rounded-xl bg-green-brand hover:bg-green-brand/90 text-white font-bold inline-flex items-center justify-center gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar checklist
        </button>
      </div>
    </div>
  );
}

function MechPanel({ slug, me, initialOrderId }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(initialOrderId || null);

  const refresh = () => m.get("/m/orders").then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { refresh(); }, []);

  const logout = async () => {
    try { await m.post("/m/logout"); } catch (e) { /* ignore */ }
    window.location.replace(`/mecanico/${slug}`);
  };

  if (openId) {
    return <ChecklistEditor orderId={openId} slug={slug} onBack={() => { setOpenId(null); refresh(); }} />;
  }

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[11px] text-zinc-500 font-mono-caps">{me.shop.name}</div>
          <div className="font-display text-xl font-black uppercase text-zinc-100">Olá, {me.employee.name.split(" ")[0]}</div>
        </div>
        <button type="button" data-testid="mech-logout" onClick={logout} className="h-9 px-3 inline-flex items-center gap-1 rounded-md border border-zinc-800 text-sm text-zinc-200"><LogOut className="h-4 w-4" /> Sair</button>
      </header>
      {list === null ? (
        <div className="text-center text-zinc-400 p-6"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <div data-testid="mech-empty" className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-sm text-zinc-300">
          Nenhuma OS está com você no momento. Fale com o dono se achar que falta alguma.
        </div>
      ) : (
        <div className="space-y-2" data-testid="mech-orders">
          {list.map((o) => (
            <button key={o.id} data-testid={`mech-order-${o.id}`} onClick={() => setOpenId(o.id)}
                    className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 hover:border-green-brand">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] text-zinc-500 font-mono-caps">OS #{o.order_number || "—"}</div>
                  <div className="font-bold text-zinc-100 truncate">{o.vehicle?.plate || "—"} · {o.vehicle?.model || ""}</div>
                  <div className="text-xs text-zinc-400 line-clamp-2">{o.description || "sem descrição"}</div>
                </div>
                <div className="text-right">
                  {o.has_checklist ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-300"><CheckCircle2 className="h-3 w-3" /> checklist feito</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-300"><AlertCircle className="h-3 w-3" /> pendente</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MechanicApp() {
  const { slug, orderId } = useParams();
  const [me, setMe] = useState(null);
  const [checked, setChecked] = useState(false);
  const location = useLocation();
  const redirectOrder = useMemo(() => {
    // Preserve orderId param through login
    const match = location.pathname.match(/\/(?:m|mecanico)\/[^/]+\/os\/([^/]+)/);
    return orderId || match?.[1] || null;
  }, [location.pathname, orderId]);

  useEffect(() => {
    m.get("/m/me").then(({ data }) => setMe(data)).catch(() => setMe(null)).finally(() => setChecked(true));
  }, []);

  if (!checked) return <div className="dark min-h-screen bg-zinc-950 text-zinc-100 p-6 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div className="dark min-h-screen bg-zinc-950 text-zinc-100">
      {!me ? <MechLogin slug={slug} redirectOrder={redirectOrder} /> : <MechPanel slug={slug} me={me} initialOrderId={redirectOrder} />}
    </div>
  );
}
